import React, { useState } from "react";
import classnames from "classnames";
import { PlanrButton, Stack } from "modules/common/components/ui";
import {
    AttachmentTile,
    Banner,
    Chip,
    CollapsibleGroup,
    ComposerBar,
    EmptyState,
    HeaderAvatar,
    HeaderIconButton,
    ListItem,
    MobileHeader,
    MobileInput,
    MonthSwitcher,
    ProgressBar,
    PullToRefresh,
    SegmentedControl,
    Sheet,
    Skeleton,
    Switch,
    TabBar,
    Toast,
} from "modules/common/components/ui/mobile";
import type { TabBarItem } from "modules/common/components/ui/mobile";
import styles from "./MobileSection.module.scss";

const tabs: TabBarItem[] = [
    { key: "home", label: "Главная", icon: "general-folder" },
    { key: "staff", label: "Штатка", icon: "general-ppl", badge: 2 },
    { key: "finance", label: "Финансы", icon: "general-credit-card" },
    { key: "tasks", label: "Задания", icon: "general-doc-check", badge: 120 },
    { key: "more", label: "Ещё", icon: "general-dots-more" },
];

const shiftMonth = (month: string, delta: -1 | 1) => {
    const [year, number] = month.split("-").map(Number);
    const date = new Date(year, number - 1 + delta, 1);

    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

/**
 * Секция «Мобильные» витрины: компоненты мобильного раздела кита (`ui/mobile`) в светлой
 * и тёмной теме рядом. Тёмная тема задана классом на обёртке, а не атрибутом страницы, —
 * значения токенов не должны выйти за пределы рамки и задеть полную версию.
 */
export const MobileSection = () => (
    <section id="mobile" className={styles.section}>
        <h2 className={styles.sectionTitle}>Мобильные</h2>
        <pre className={styles.code}>
            {`import { TabBar, MobileHeader, Banner, Toast, Sheet, ListItem, MobileInput, EmptyState, Skeleton, PullToRefresh, SegmentedControl, Chip, Switch, ProgressBar, CollapsibleGroup, AttachmentTile, ComposerBar, MonthSwitcher }\n    from "modules/common/components/ui/mobile";   // не из ui/index.ts: тот тянет Blueprint`}
        </pre>
        <p className={styles.note}>
            Компоненты без Blueprint для мобильной версии (<code>/mobile</code>). Цвета — только CSS-переменные
            <code> --m-*</code> и <code>--planr-*</code>; зоны касания не меньше 44×44, ширина экрана — 360 px.
        </p>
        <div className={styles.frames}>
            <Frame theme="light" title="Светлая тема" />
            <Frame theme="dark" title="Тёмная тема" />
        </div>
    </section>
);

const Frame = ({ theme, title }: { theme: "light" | "dark"; title: string }) => {
    const [active, setActive] = useState("staff");
    const [name, setName] = useState("");
    const [updates, setUpdates] = useState(0);
    const [segment, setSegment] = useState("requests");
    const [month, setMonth] = useState("2026-10");
    const [chipMine, setChipMine] = useState(true);
    const [chipOverdue, setChipOverdue] = useState(false);
    const [calendarOn, setCalendarOn] = useState(true);
    const [comment, setComment] = useState("");

    return (
        <div className={styles.frameWrap}>
            <div className={styles.frameTitle}>{title}</div>
            <div className={classnames(styles.frame, theme === "dark" ? styles.dark : styles.light)}>
                <MobileHeader title="Профиль" onBack={() => undefined}>
                    <HeaderIconButton icon="general-bell" label="Уведомления" counter={3} onClick={() => undefined} />
                    <HeaderAvatar name="Иванов Иван Иванович" onClick={() => undefined} />
                </MobileHeader>

                <Banner kind="offline" text="Нет сети" />
                <Banner kind="unsent" text="Не отправлено: 2" />
                <Banner kind="update" text="Доступна новая версия" actionLabel="Обновить" onAction={() => undefined} />

                <div className={styles.body}>
                    <div className={styles.group}>
                        <ListItem title="Календарь" icon="general-calendar" onClick={() => undefined} />
                        <ListItem title="Профиль и настройки" subtitle="Иванов Иван Иванович" icon="general-contact" onClick={() => undefined} />
                        <ListItem title="Версия" right="1.42.0" />
                        <ListItem title="Выйти" icon="general-sign-out" danger onClick={() => undefined} />
                    </div>

                    <PullToRefresh
                        onRefresh={() =>
                            new Promise<void>((resolve) =>
                                setTimeout(() => {
                                    setUpdates((n) => n + 1);
                                    resolve();
                                }, 1200)
                            )
                        }
                    >
                        <ListItem title="Потяните вниз (на телефоне)" subtitle={`Обновлений: ${updates}`} />
                    </PullToRefresh>

                    <MobileInput label="Логин" value={name} onChange={setName} placeholder="Введите логин" />
                    <MobileInput label="Пароль" type="password" value="123" onChange={() => undefined} error="Неверный логин или пароль" />

                    <SegmentedControl
                        ariaLabel="Режим"
                        items={[
                            { key: "requests", label: "Заявки" },
                            { key: "summary", label: "Сводка" },
                        ]}
                        value={segment}
                        onChange={setSegment}
                    />

                    <MonthSwitcher month={month} onChange={(delta) => setMonth(shiftMonth(month, delta))} />

                    <Stack gap="s">
                        <Chip label="Мои" pressed={chipMine} onChange={setChipMine} />
                        <Chip label="Просроченные" pressed={chipOverdue} onChange={setChipOverdue} />
                    </Stack>

                    <Switch
                        label="Календарь"
                        hint="Напоминания и новые события"
                        checked={calendarOn}
                        onChange={setCalendarOn}
                    />

                    <ProgressBar value={0.64} label="Исполнено 64%" />
                    <ProgressBar value={1.3} overflow label="Бюджет" />

                    <CollapsibleGroup title="Сегодня" count={2}>
                        <ListItem title="Заявка № 1" />
                        <ListItem title="Заявка № 2" />
                    </CollapsibleGroup>
                    <CollapsibleGroup title="Ранее" count={5} defaultCollapsed>
                        <ListItem title="Заявка № 3" />
                    </CollapsibleGroup>

                    <AttachmentTile name="Схема.pdf" size={1.2 * 1024 * 1024} onRemove={() => undefined} />
                    <AttachmentTile name="Фото.jpg" size={350 * 1024} state="uploading" progress={0.4} onRemove={() => undefined} />
                    <AttachmentTile name="Смета.xlsx" size={90 * 1024} state="error" onRetry={() => undefined} onRemove={() => undefined} />

                    <ComposerBar value={comment} onChange={setComment} onSend={() => setComment("")} label="Комментарий" placeholder="Комментарий" />
                    <ComposerBar value="Текст" onChange={() => undefined} onSend={() => undefined} label="Комментарий без сети" disabledHint="Нужна сеть" />
                    <ComposerBar
                        value=""
                        onChange={() => undefined}
                        onSend={() => undefined}
                        label="Комментарий с файлом"
                        placeholder="Комментарий"
                        leading={
                            <button type="button" aria-label="Прикрепить" style={{ minWidth: 44, minHeight: 48 }}>
                                📎
                            </button>
                        }
                        top={<AttachmentTile name="Узел.jpg" size={1200 * 1024} state="done" onRemove={() => undefined} />}
                        canSend={true}
                    />

                    <Stack gap="s">
                        <PlanrButton type="primary" size="small">
                            Вход
                        </PlanrButton>
                        <PlanrButton type="secondary" size="small">
                            Позже
                        </PlanrButton>
                        <PlanrButton type="graybtn" size="small" disabled>
                            Недоступна
                        </PlanrButton>
                    </Stack>

                    <Stack gap="m" align="center">
                        <Skeleton width={48} height={48} circle />
                        <Stack direction="column" gap="xs" grow>
                            <Skeleton width="70%" height={16} />
                            <Skeleton width="40%" height={12} />
                        </Stack>
                    </Stack>

                    <div className={styles.group}>
                        <EmptyState title="Нет доступа" description="У вас нет прав на этот раздел" icon="general-exclamation" />
                    </div>

                    <Toast text="Установлена последняя версия" inline />

                    <Sheet open inline onClose={() => undefined} title="Установить приложение">
                        <ListItem title="Установить" icon="general-download" onClick={() => undefined} />
                        <ListItem title="Позже" icon="general-clock" onClick={() => undefined} />
                    </Sheet>
                </div>

                <TabBar items={tabs} activeKey={active} onSelect={setActive} />
            </div>
        </div>
    );
};
